import { useMemo, useState } from "react";

import type { Activity } from "~/v2/api/rest/account";
import type { OverviewActivityFilter, OverviewActivityRow } from "../types";
import {
  matchesOverviewFilter,
  OVERVIEW_ACTIVITY_ROW_LIMIT,
  toOverviewActivityRow,
} from "./overviewActivities.utils";

export interface UseRecentActivityCardControllerParams {
  activities: Activity[];
  isAccountReady: boolean;
  isLoading: boolean;
  hasError: boolean;
}

export interface RecentActivityCardControllerValue {
  rows: OverviewActivityRow[];
  totalCount: number;
  filter: OverviewActivityFilter;
  onFilterChange: (filter: OverviewActivityFilter) => void;
  rowLimit: number;
  emptyMessage: string;
}

const emptyMessage = (
  isAccountReady: boolean,
  isLoading: boolean,
  hasError: boolean,
  filter: OverviewActivityFilter,
): string => {
  if (!isAccountReady) {
    return "Select an account to view activity.";
  }
  if (hasError) {
    return "Activity could not be loaded.";
  }
  if (isLoading) {
    return "Loading activity…";
  }
  if (filter === "all") {
    return "No activity in the last 30 days.";
  }
  if (filter === "corporate") {
    return "No corporate actions in the last 30 days.";
  }
  return `No ${filter} activity in the last 30 days.`;
};

export const useRecentActivityCardController = ({
  activities,
  isAccountReady,
  isLoading,
  hasError,
}: UseRecentActivityCardControllerParams): RecentActivityCardControllerValue => {
  const [filter, setFilter] = useState<OverviewActivityFilter>("all");

  const filtered = useMemo(
    () =>
      activities.filter((activity) => matchesOverviewFilter(activity, filter)),
    [activities, filter],
  );

  const rows = useMemo(
    () =>
      filtered
        .slice(0, OVERVIEW_ACTIVITY_ROW_LIMIT)
        .map((activity, index) => toOverviewActivityRow(activity, index)),
    [filtered],
  );

  return {
    rows,
    totalCount: filtered.length,
    filter,
    onFilterChange: setFilter,
    rowLimit: OVERVIEW_ACTIVITY_ROW_LIMIT,
    emptyMessage: emptyMessage(isAccountReady, isLoading, hasError, filter),
  };
};
